import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLang } from "@/LanguageContext";
import { ChapterLabel } from "@/components/Reveal";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { IMAGES } from "@/content";
import { trackEvent } from "@/lib/track";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldCls =
  "w-full bg-transparent border-0 border-b border-white/25 rounded-none px-0 py-3 text-white placeholder:text-white/40 focus-visible:ring-0 focus-visible:border-gold transition-colors";

export default function ContactForm() {
  const { t } = useLang();
  const c = t.contact;
  const [range, setRange] = useState(undefined);
  const [budget, setBudget] = useState("");
  const [loading, setLoading] = useState(false);
  const [prefill, setPrefill] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ftc_search");
      sessionStorage.removeItem("ftc_search");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [form, setForm] = useState(() => {
    const base = { name: "", email: "", phone: "", guests: "", destinations: "", interests: "", special_requests: "" };
    if (prefill) {
      base.destinations = prefill.placeLabel || "";
      base.guests = prefill.guests || "";
      base.special_requests = prefill.summary || "";
    }
    return base;
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onSearch = (e) => {
      const p = e.detail;
      setPrefill(p);
      setForm((f2) => ({ ...f2, destinations: p.placeLabel || "", guests: p.guests || "", special_requests: p.summary || "" }));
    };
    window.addEventListener("ftc:search", onSearch);
    return () => window.removeEventListener("ftc:search", onSearch);
  }, []);

  const datesLabel = range?.from
    ? range.to
      ? `${format(range.from, "PP")} — ${format(range.to, "PP")}`
      : format(range.from, "PP")
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(c.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        budget,
        travel_dates: datesLabel,
      });
      toast.success(c.success);
      trackEvent("inquiry_submit");
      setForm({ name: "", email: "", phone: "", guests: "", destinations: "", interests: "", special_requests: "" });
      setRange(undefined);
      setBudget("");
    } catch (err) {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-navy py-28 md:py-40 overflow-hidden">
      <div className="absolute right-0 top-0 bottom-0 w-1/3 hidden lg:block opacity-30">
        <img src={IMAGES.champagne} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy to-transparent" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 md:px-10">
        <ChapterLabel chapter={c.chapter} />
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="font-serif-lux text-white text-4xl sm:text-5xl lg:text-6xl font-light"
        >
          {c.title}
        </motion.h2>
        <p className="text-white/60 mt-5 font-light max-w-xl">{c.sub}</p>
        {prefill && (
          <p data-testid="search-prefill-note" className="mt-6 border border-gold/40 bg-gold/10 text-gold-light text-sm font-light px-4 py-3 max-w-xl">
            {prefill.summary}
          </p>
        )}

        <form onSubmit={submit} data-testid="inquiry-form" className="mt-14 grid sm:grid-cols-2 gap-x-10 gap-y-8">
          <FloatField label={c.f.name}><Input data-testid="input-name" value={form.name} onChange={upd("name")} className={fieldCls} /></FloatField>
          <FloatField label={c.f.email}><Input data-testid="input-email" type="email" value={form.email} onChange={upd("email")} className={fieldCls} /></FloatField>
          <FloatField label={c.f.phone}><Input data-testid="input-phone" value={form.phone} onChange={upd("phone")} className={fieldCls} /></FloatField>

          <FloatField label={c.f.dates}>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="input-dates" className={`${fieldCls} flex items-center justify-between text-left`}>
                  <span className="text-white/90">{datesLabel || ""}</span>
                  <CalendarIcon className="h-4 w-4 text-gold" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-ivory border-gold/30" align="start">
                <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} initialFocus />
              </PopoverContent>
            </Popover>
          </FloatField>

          <FloatField label={c.f.guests}><Input data-testid="input-guests" value={form.guests} onChange={upd("guests")} className={fieldCls} /></FloatField>

          <FloatField label={c.f.budget}>
            <Select value={budget} onValueChange={setBudget}>
              <SelectTrigger data-testid="input-budget" className={`${fieldCls} text-white/90 [&>span]:text-white/90`}>
                <SelectValue placeholder="" />
              </SelectTrigger>
              <SelectContent className="bg-ivory border-gold/30">
                {c.budgets.map((b) => (
                  <SelectItem key={b} value={b} data-testid={`budget-${b}`}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FloatField>

          <FloatField label={c.f.destinations} full><Input data-testid="input-destinations" value={form.destinations} onChange={upd("destinations")} className={fieldCls} /></FloatField>
          <FloatField label={c.f.interests} full><Input data-testid="input-interests" value={form.interests} onChange={upd("interests")} className={fieldCls} /></FloatField>
          <FloatField label={c.f.requests} full>
            <Textarea data-testid="input-requests" value={form.special_requests} onChange={upd("special_requests")} rows={3} className={`${fieldCls} resize-none`} />
          </FloatField>

          <div className="sm:col-span-2 mt-4">
            <button
              type="submit"
              data-testid="submit-inquiry"
              disabled={loading}
              className="btn-lux w-full sm:w-auto bg-gold text-navy hover:bg-gold-light px-10 py-4 text-xs tracking-luxe uppercase font-medium disabled:opacity-60"
            >
              {loading ? "…" : c.cta}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const FloatField = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-gold/80 text-[11px] tracking-luxe uppercase mb-1">{label}</label>
    {children}
  </div>
);
